import React from "react";
import { useQuery } from "@apollo/client";
import { useParams } from "react-router-dom";

import dayjs from "dayjs";
import clsx from "clsx";

import { useTranslation } from "react-i18next";

import Accordion from "react-bootstrap/Accordion";
import Card from "react-bootstrap/Card";

import { QUERY_UPCOMING_MEETING_ATTENDEE } from "../../../../config/graphql/query";

const statusBadgeClass = (
  status: PreRegistrationTokenStatus | "no_token",
): string => {
  switch (status) {
    case "completed":
      return "badge badge-success";
    case "pending":
      return "badge badge-warning";
    case "expired":
      return "badge badge-danger";
    case "no_token":
    default:
      return "badge badge-light";
  }
};

const PreRegistration = React.memo(() => {
  const { id } = useParams<{ id: string }>();

  const { t } = useTranslation(["preRegistrations", "common"]);

  const { data } = useQuery<{
    upcomingMeetingAttendee?: IUpcomingMeetingAttendee | null;
  }>(QUERY_UPCOMING_MEETING_ATTENDEE, {
    skip: !id,
    variables: { id },
    fetchPolicy: "network-only",
  });

  const item = data?.upcomingMeetingAttendee;

  if (!item) {
    return (
      <div className="container-fluid">
        <Card className="mb-3 p-3">{t("preRegistrations:notFound")}</Card>
      </div>
    );
  }

  const { attendee, meeting, hasCheckedIn } = item;
  const token = attendee.preRegistrationToken;
  const status = token?.status || "no_token";
  const visitorFields = attendee.visitorFields || [];

  return (
    <div className="container-fluid">
      <h6 className="mb-3">{t("preRegistrations:heading.primary")}</h6>

      <Card className="mb-3">
        <Card.Header className="p-3 d-flex justify-content-between align-items-center">
          <span>{t("preRegistrations:section.visitor")}</span>
          <span>
            <span className={statusBadgeClass(status)}>
              {t(`preRegistrations:status.${status}`)}
            </span>
            {hasCheckedIn && (
              <span className="badge badge-info ml-2">
                {t("preRegistrations:status.checkedIn")}
              </span>
            )}
          </span>
        </Card.Header>
        <Card.Body className="p-3">
          {t("preRegistrations:visitor.fullName")}
          {attendee.fullName}
        </Card.Body>
        <Card.Body className="p-3">
          {t("preRegistrations:visitor.email")}
          {attendee.email}
        </Card.Body>
        {attendee.phone && (
          <Card.Body className="p-3">
            {t("preRegistrations:visitor.phone")}
            {attendee.phone}
          </Card.Body>
        )}
      </Card>

      {visitorFields.length > 0 && (
        <Accordion defaultActiveKey="0" className="mb-3">
          <Card>
            <Accordion.Toggle as={Card.Header} eventKey="0" className="p-3">
              {t("preRegistrations:section.submittedFields")}
            </Accordion.Toggle>
            {visitorFields.map((field) => {
              let fieldValue = field.value;

              if (field.value === "true") {
                fieldValue = "✓";
              }

              if (field.value === "false") {
                fieldValue = "❌";
              }

              return (
                <Accordion.Collapse eventKey="0" key={field.name}>
                  <Card.Body className="p-3">
                    {field.name} :{" "}
                    <span
                      className={clsx("", {
                        "text-success lead font-weight-bold":
                          field.value === "true",
                      })}
                    >
                      {fieldValue}
                    </span>
                  </Card.Body>
                </Accordion.Collapse>
              );
            })}
          </Card>
        </Accordion>
      )}

      {token && (
        <Accordion defaultActiveKey="0" className="mb-3">
          <Card>
            <Accordion.Toggle as={Card.Header} eventKey="0" className="p-3">
              {t("preRegistrations:section.token")}
            </Accordion.Toggle>
            <Accordion.Collapse eventKey="0">
              <Card.Body className="p-3">
                {t("preRegistrations:token.expiresAt")}
                {token.expiresAt
                  ? dayjs(token.expiresAt).local().format("DD-MM-YYYY / HH:mm")
                  : "—"}
              </Card.Body>
            </Accordion.Collapse>
            <Accordion.Collapse eventKey="0">
              <Card.Body className="p-3">
                {t("preRegistrations:token.stepsProgress", {
                  completed: token.completedStepsCount,
                  total: token.totalStepsCount,
                })}
              </Card.Body>
            </Accordion.Collapse>
            {token.hasMissingRequiredFields && (
              <Accordion.Collapse eventKey="0">
                <Card.Body className="p-3 text-warning">
                  ⚠ {t("preRegistrations:token.missingRequired")}
                </Card.Body>
              </Accordion.Collapse>
            )}
          </Card>
        </Accordion>
      )}

      <Accordion defaultActiveKey="0" className="mb-3">
        <Card>
          <Accordion.Toggle as={Card.Header} eventKey="0" className="p-3">
            {t("preRegistrations:section.meeting")}
          </Accordion.Toggle>
          <Accordion.Collapse eventKey="0">
            <Card.Body className="p-3">
              {t("preRegistrations:meeting.organizerFullName")}
              {meeting.organizer?.fullName || ""}
            </Card.Body>
          </Accordion.Collapse>
          <Accordion.Collapse eventKey="0">
            <Card.Body className="p-3">
              {t("preRegistrations:meeting.organizerEmail")}
              {meeting.organizer?.email || ""}
            </Card.Body>
          </Accordion.Collapse>
          {meeting.subject && (
            <Accordion.Collapse eventKey="0">
              <Card.Body className="p-3">
                {t("preRegistrations:meeting.subject")}
                {meeting.subject}
              </Card.Body>
            </Accordion.Collapse>
          )}
          <Accordion.Collapse eventKey="0">
            <Card.Body className="p-3">
              {t("preRegistrations:meeting.dtstart")}
              {dayjs(meeting.dtstart).local().format("DD-MM-YYYY / HH:mm")}
            </Card.Body>
          </Accordion.Collapse>
          {meeting.dtend && (
            <Accordion.Collapse eventKey="0">
              <Card.Body className="p-3">
                {t("preRegistrations:meeting.dtend")}
                {dayjs(meeting.dtend).local().format("DD-MM-YYYY / HH:mm")}
              </Card.Body>
            </Accordion.Collapse>
          )}
          {meeting.location && (
            <Accordion.Collapse eventKey="0">
              <Card.Body className="p-3">
                {t("preRegistrations:meeting.location")}
                {meeting.location}
              </Card.Body>
            </Accordion.Collapse>
          )}
        </Card>
      </Accordion>
    </div>
  );
});

export default PreRegistration;
